import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, Trash2, Download, Users, Mail, Plus, Footprints } from 'lucide-react';
import { CaminoStats } from '../components/CaminoStats';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CATEGORIES = [
  { value: 'grammar', label: 'Gramática' },
  { value: 'vocabulary', label: 'Vocabulario' },
  { value: 'exercises', label: 'Ejercicios' },
  { value: 'guides', label: 'Guías' },
];

export default function AdminMaterials({ onSessionExpired }) {
  const { token } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [caminoStats, setCaminoStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('materials'); // 'materials' | 'leads'
  const [leadFilter, setLeadFilter] = useState('all');
  const [uploading, setUploading] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState('A1');
  const [category, setCategory] = useState('grammar');
  const [file, setFile] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const handle401 = (e) => {
    if (e.response?.status === 401) { onSessionExpired && onSessionExpired(); return true; }
    return false;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [mRes, lRes, cRes] = await Promise.all([
        axios.get(`${API}/materials`),
        axios.get(`${API}/leads`, authHeaders),
        axios.get(`${API}/camino/stats`, authHeaders),
      ]);
      setMaterials(mRes.data);
      setLeads(lRes.data);
      setCaminoStats(cRes.data);
    } catch (e) {
      if (!handle401(e)) toast.error('Error al cargar los materiales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Selecciona un archivo PDF'); return; }
    if (file.type !== 'application/pdf') { toast.error('El archivo debe ser un PDF'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('level', level);
      formData.append('category', category);
      formData.append('file', file);
      await axios.post(`${API}/materials`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success('¡Material subido correctamente!');
      setTitle(''); setDescription(''); setLevel('A1'); setCategory('grammar'); setFile(null);
      document.getElementById('material-file-input').value = '';
      fetchAll();
    } catch (e) {
      if (!handle401(e)) toast.error(e.response?.data?.detail || 'Error al subir el material');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este material?')) return;
    try {
      await axios.delete(`${API}/materials/${id}`, authHeaders);
      toast.success('Material eliminado');
      setMaterials(materials.filter((m) => m.id !== id));
    } catch (e) {
      if (!handle401(e)) toast.error('Error al eliminar');
    }
  };

  const SOURCE_META = {
    newsletter: { label: 'Newsletter', cls: 'bg-[#FFF0EF] text-[#E38987]' },
    level_test: { label: 'Test de nivel', cls: 'bg-[#EEF7EA] text-[#6BA368]' },
    camino_download: { label: 'Camino', cls: 'bg-[#FFF4E5] text-[#D9903A]' },
    material_download: { label: 'Descarga', cls: 'bg-[#E8F5FA] text-[#7DBDD9]' },
    vocabulario_download: { label: 'Vocabulario', cls: 'bg-[#EDE9FB] text-[#7A6BC4]' },
  };
  const sourceOf = (l) => SOURCE_META[l.source] || SOURCE_META.material_download;
  const detailOf = (l) => (l.source === 'newsletter' ? 'Newsletter' : (l.source === 'level_test' || l.source === 'camino_download') ? l.material_title : `Descargó: ${l.material_title}`);
  const filteredLeads = leadFilter === 'all' ? leads : leads.filter((l) => (l.source || 'material_download') === leadFilter);
  const countBy = (s) => leads.filter((l) => (l.source || 'material_download') === s).length;

  const exportLeadsCSV = () => {
    const rows = [['Email', 'Nombre', 'Origen', 'Detalle', 'Fecha', 'Comunicaciones', 'Fecha consentimiento']];
    filteredLeads.forEach((l) => rows.push([
      l.email,
      l.name || '',
      sourceOf(l).label,
      l.material_title || '',
      new Date(l.created_at).toLocaleDateString('es-ES'),
      l.marketing_consent ? 'Sí' : 'No',
      l.consent_date ? new Date(l.consent_date).toLocaleDateString('es-ES') : '',
    ]));
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const suffix = leadFilter === 'all' ? '' : `_${leadFilter}`;
    a.href = url; a.download = `contactos_spanischenlared${suffix}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" /></div>;
  }

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setView('materials')}
          data-testid="subtab-materials"
          className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${view === 'materials' ? 'bg-[#7DBDD9] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
        >
          <FileText className="w-4 h-4" /><span>Materiales ({materials.length})</span>
        </button>
        <button
          onClick={() => setView('leads')}
          data-testid="subtab-leads"
          className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${view === 'leads' ? 'bg-[#7DBDD9] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
        >
          <Users className="w-4 h-4" /><span>Contactos ({leads.length})</span>
        </button>
        <button
          onClick={() => setView('camino')}
          data-testid="subtab-camino"
          className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${view === 'camino' ? 'bg-[#7DBDD9] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
        >
          <Footprints className="w-4 h-4" /><span>Camino ({caminoStats.reduce((a, s) => a + s.total, 0)})</span>
        </button>
      </div>

      {view === 'camino' && <CaminoStats stats={caminoStats} />}

      {view === 'materials' && (
        <>
          {/* Upload form */}
          <form onSubmit={handleUpload} className="bg-white rounded-2xl border-2 border-[#A8D8EA]/40 p-6 mb-8 space-y-4" data-testid="material-upload-form">
            <h3 className="text-lg font-bold text-[#4A4A4A] flex items-center gap-2"><Plus className="w-5 h-5 text-[#7DBDD9]" />Subir nuevo material</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título del material" data-testid="material-title-input" className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none" />
              <div className="flex gap-3">
                <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="material-level-select" className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none">
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="material-category-select" className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <textarea required value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descripción breve" rows={2} data-testid="material-description-input" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none resize-none" />
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <input id="material-file-input" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="material-file-input" className="text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E8F5FA] file:text-[#7DBDD9] file:font-semibold" />
              <button type="submit" disabled={uploading} data-testid="material-upload-btn" className="px-6 py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-2">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" />Subiendo...</> : <><Upload className="w-4 h-4" />Subir PDF</>}
              </button>
            </div>
          </form>

          {/* Materials list */}
          {materials.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="no-materials">
              <FileText className="w-14 h-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aún no has subido materiales</p>
            </div>
          ) : (
            <div className="space-y-3">
              {materials.map((m, i) => (
                <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  data-testid={`material-item-${i}`}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 bg-[#FFF0EF] rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#E38987]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#4A4A4A] truncate">{m.title}</p>
                      <p className="text-xs text-gray-500 truncate">{m.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-[#E8F5FA] text-[#7DBDD9]">{m.level}</span>
                        <span className="text-xs text-gray-400 flex items-center gap-1"><Download className="w-3 h-3" />{m.downloads}</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(m.id)} data-testid={`material-delete-${i}`} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'leads' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-[#4A4A4A]">Base de datos de contactos</h3>
              <p className="text-sm text-gray-500">Emails capturados en el test de nivel, descargas de materiales y newsletter</p>
            </div>
            {leads.length > 0 && (
              <button onClick={exportLeadsCSV} data-testid="export-leads-btn" className="flex items-center gap-2 px-4 py-2 bg-[#7DBDD9] text-white rounded-xl text-sm font-semibold hover:bg-[#5DA8CC] transition-colors">
                <Download className="w-4 h-4" />Exportar CSV{leadFilter !== 'all' ? ` (${filteredLeads.length})` : ''}
              </button>
            )}
          </div>
          {leads.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="lead-filters">
              {[['all', 'Todos', leads.length], ['level_test', 'Test de nivel', countBy('level_test')], ['camino_download', 'Camino', countBy('camino_download')], ['material_download', 'Descargas', countBy('material_download')], ['vocabulario_download', 'Vocabulario', countBy('vocabulario_download')], ['newsletter', 'Newsletter', countBy('newsletter')]].map(([key, label, n]) => (
                <button key={key} onClick={() => setLeadFilter(key)} data-testid={`lead-filter-${key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${leadFilter === key ? 'bg-[#4A4A4A] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'}`}>
                  {label} · {n}
                </button>
              ))}
            </div>
          )}
          {leads.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="no-leads">
              <Users className="w-14 h-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aún no hay contactos capturados</p>
              <p className="text-gray-400 text-sm mt-1">Cuando alguien haga el test de nivel o descargue un material, su email aparecerá aquí.</p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100" data-testid="no-filtered-leads">
              <p className="text-gray-500">No hay contactos de este origen todavía</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {filteredLeads.map((l, i) => (
                <div key={l.id} data-testid={`lead-item-${i}`} className={`flex items-center justify-between p-4 gap-4 ${i !== filteredLeads.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-[#E8F5FA] rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-[#7DBDD9]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#4A4A4A] truncate">{l.email}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {l.name ? `${l.name} · ` : ''}
                        {detailOf(l)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span
                      data-testid={`lead-consent-${i}`}
                      title="Consentimiento para comunicaciones comerciales"
                      className={`px-2 py-0.5 rounded-md text-xs font-semibold ${l.marketing_consent ? 'bg-[#EEF7EA] text-[#6BA368]' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {l.marketing_consent ? 'Comunicaciones: Sí' : 'Comunicaciones: No'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${sourceOf(l).cls}`}>
                      {sourceOf(l).label}
                    </span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString('es-ES')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
